import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';
import {
  ApPaymentRequest,
  ApPaymentRequestInsert,
  ApPaymentRequestSearchCommand,
  ApPaymentRequestUpdate,
} from '../models/ap-payment-request/ap-payment-request';
import { ApPayment } from '../models/ap-payment/ap-payment';
import { PagedResult } from './bank-account.proxy.service';

@Injectable({ providedIn: 'root' })
export class ApPaymentRequestProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/ap/payment-requests';

  public getById(id: string): Observable<ApiResponse<ApPaymentRequest>> {
    return this.httpClient.get<ApiResponse<ApPaymentRequest>>(`${this.baseUrl}/${id}`);
  }

  public createDraft(data: ApPaymentRequestInsert): Observable<ApiResponse<ApPaymentRequest>> {
    return this.httpClient.post<ApiResponse<ApPaymentRequest>>(this.baseUrl, data);
  }

  public update(id: string, data: ApPaymentRequestUpdate): Observable<ApiResponse<ApPaymentRequest>> {
    return this.httpClient.put<ApiResponse<ApPaymentRequest>>(`${this.baseUrl}/${id}`, data);
  }

  public submit(id: string): Observable<ApiResponse<ApPaymentRequest>> {
    return this.httpClient.post<ApiResponse<ApPaymentRequest>>(`${this.baseUrl}/${id}/submit`, {});
  }

  public approve(id: string): Observable<ApiResponse<ApPaymentRequest>> {
    return this.httpClient.post<ApiResponse<ApPaymentRequest>>(`${this.baseUrl}/${id}/approve`, {});
  }

  public reject(id: string): Observable<ApiResponse<ApPaymentRequest>> {
    return this.httpClient.post<ApiResponse<ApPaymentRequest>>(`${this.baseUrl}/${id}/reject`, {});
  }

  public cancel(id: string): Observable<ApiResponse<ApPaymentRequest>> {
    return this.httpClient.post<ApiResponse<ApPaymentRequest>>(`${this.baseUrl}/${id}/cancel`, {});
  }

  /** Open (or spawn) the 1:1 Draft ApPayment for an Approved request. Idempotent: returns the same payment on repeat calls. */
  public createOrGetPayment(id: string): Observable<ApiResponse<ApPayment>> {
    return this.httpClient.post<ApiResponse<ApPayment>>(`${this.baseUrl}/${id}/payment`, {});
  }

  public search(
    command: ApPaymentRequestSearchCommand,
    pageNumber = 1,
    pageSize = 25,
    sortField?: string,
    sortOrder = -1,
  ): Observable<ApiResponse<PagedResult<ApPaymentRequest>>> {
    let params = new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize).set('sortOrder', sortOrder);
    if (sortField) params = params.set('sortField', sortField);
    return this.httpClient.post<ApiResponse<PagedResult<ApPaymentRequest>>>(`${this.baseUrl}/search`, command, { params });
  }

  /**
   * Deletes a DRAFT document (#5223). The API refuses anything further along, so this is safe to offer
   * from a draft screen and safe to have refused if the status moved on in another tab.
   */
  public delete(id: string): Observable<ApiResponse<string>> {
    return this.httpClient.delete<ApiResponse<string>>(`${this.baseUrl}/${id}`);
  }
}
